/**
 * @fileoverview Table of variants with no cost set, with inline cost editing.
 * @module features/sku-alias/components/MissingCostTable
 */

import { memo, useCallback, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2 } from 'lucide-react'
import { format } from 'date-fns'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { CostInput, DatePicker, parseCostToNumber } from '@/shared/components/ui'
import type { Variant } from '@/lib/variantService'
import type { UpdateVariantCostPayload } from '@/features/sku-alias/api/missingCost'

const DEFAULT_CURRENCY = 'PLN'

/** Props for {@link MissingCostTable}. */
export interface MissingCostTableProps {
  /** Variants missing a cost. */
  variants: Variant[]
  /** Called when a row's cost is saved. */
  onSave: (payload: UpdateVariantCostPayload) => void
  /** Id of the variant currently being saved, if any. */
  savingId: number | null
}

interface CostCellProps {
  variant: Variant
  onSave: (payload: UpdateVariantCostPayload) => void
  isSaving: boolean
}

/**
 * A single editable row. Memoized so typing in one row does not re-render the
 * whole table.
 */
const CostRow = memo(function CostRow({ variant, onSave, isSaving }: CostCellProps): ReactElement {
  const { t } = useTranslation('features-sku-alias')
  const [costValue, setCostValue] = useState('')
  const [currencyValue, setCurrencyValue] = useState(variant.originalCurrency ?? DEFAULT_CURRENCY)
  const [costDate, setCostDate] = useState(variant.costDate ?? '')

  const parsedCost = parseCostToNumber(costValue)
  const canSave = parsedCost != null && parsedCost > 0 && costDate.length > 0 && !isSaving

  const handleSave = useCallback((): void => {
    if (parsedCost == null || parsedCost <= 0 || costDate.length === 0) return
    onSave({
      id: variant.id,
      originalCost: parsedCost,
      originalCurrency: currencyValue || DEFAULT_CURRENCY,
      costDate,
    })
  }, [parsedCost, costDate, currencyValue, onSave, variant.id])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      if (canSave) handleSave()
    },
    [canSave, handleSave]
  )

  return (
    <TableRow>
      <TableCell className="py-4 pl-6">
        <div className="min-w-0 space-y-0.5">
          <span className="block max-w-[22rem] truncate font-medium">
            {variant.productName ?? variant.name}
          </span>
          <span className="block max-w-[22rem] truncate font-mono text-xs text-muted-foreground">
            {variant.sku}
          </span>
        </div>
      </TableCell>
      <TableCell className="w-56 py-4">
        <CostInput
          hideLabel
          costValue={costValue}
          onCostChange={setCostValue}
          onCostKeyDown={handleKeyDown}
          currencyValue={currencyValue}
          onCurrencyChange={setCurrencyValue}
          disabled={isSaving}
        />
      </TableCell>
      <TableCell className="w-44 py-4">
        <DatePicker
          value={costDate ? new Date(costDate) : null}
          onChange={date => setCostDate(date ? format(date, 'yyyy-MM-dd') : '')}
          disabled={isSaving}
        />
      </TableCell>
      <TableCell className="w-20 py-4 pr-6 text-right">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSave}
          disabled={!canSave}
          aria-label={t('needsReview.costReview.table.actions.save')}
          className="ml-auto text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
        </Button>
      </TableCell>
    </TableRow>
  )
})

/**
 * Renders variants missing a cost with an inline cost/currency/date editor per row.
 *
 * @component
 */
export function MissingCostTable({
  variants,
  onSave,
  savingId,
}: MissingCostTableProps): ReactElement {
  const { t } = useTranslation('features-sku-alias')

  return (
    <div className="max-w-full overflow-x-auto rounded-3xl border border-border/15 bg-card">
      <Table>
        <TableHeader>
          <TableRow className="[&_th]:h-14">
            <TableHead className="whitespace-nowrap pl-6 text-xs font-semibold tracking-wide text-muted-foreground">
              {t('needsReview.costReview.table.columns.variant')}
            </TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground">
              {t('needsReview.costReview.table.columns.cost')}
            </TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground">
              {t('needsReview.costReview.table.columns.costDate')}
            </TableHead>
            <TableHead className="w-20 whitespace-nowrap pr-6 text-right text-xs font-semibold tracking-wide text-muted-foreground">
              {t('needsReview.costReview.table.columns.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {variants.map(variant => (
            <CostRow
              key={variant.id}
              variant={variant}
              onSave={onSave}
              isSaving={savingId === variant.id}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default MissingCostTable
